/**
 * CNPJ numérico e alfanumérico (Receita Federal / IN RFB 2.229).
 * Formato: 14 posições — 12 primeiros [A-Z0-9], 2 últimos dígitos verificadores.
 * DV: módulo 11 com valor ASCII − 48 (compatível com CNPJ numérico legado).
 */

export const CNPJ_MASK = 'NN.NNN.NNN/NNNN-##';
export const CPF_MASK = '###.###.###-##';
export const CPF_CNPJ_MASKS = [CPF_MASK, CNPJ_MASK];

const CNPJ_PATTERN = /^[A-Z0-9]{12}[0-9]{2}$/;

export function keepAlphanumericUpper(texto) {
  if (texto == null || texto === '') {
    return null;
  }
  const cleaned = String(texto).replace(/[^0-9A-Za-z]/g, '');
  return cleaned === '' ? null : cleaned.toUpperCase();
}

export function normalizeCNPJ(cnpj) {
  const cleaned = keepAlphanumericUpper(cnpj);
  if (cleaned == null) {
    return null;
  }
  return cleaned.padStart(14, '0');
}

export function normalizeCpfCnpj(cpfCnpj) {
  if (cpfCnpj == null || String(cpfCnpj).trim() === '') {
    return null;
  }
  const cleaned = keepAlphanumericUpper(cpfCnpj);
  if (cleaned == null) {
    return null;
  }
  const looksLikeCnpj = cleaned.length > 11 || /[A-Z]/.test(cleaned);
  if (looksLikeCnpj) {
    return cleaned.padStart(14, '0');
  }
  return cleaned.replace(/\D/g, '').padStart(11, '0');
}

export function formatCNPJ(cnpj) {
  if (cnpj == null) {
    return '';
  }
  const clean = keepAlphanumericUpper(cnpj);
  if (clean == null || clean.length !== 14) {
    return cnpj;
  }
  return (
    clean.substring(0, 2) + '.' +
    clean.substring(2, 5) + '.' +
    clean.substring(5, 8) + '/' +
    clean.substring(8, 12) + '-' +
    clean.substring(12, 14)
  );
}

/**
 * Máscara progressiva para digitação de CPF ou CNPJ (alfanumérico).
 * Letras ou mais de 11 caracteres → CNPJ; caso contrário → CPF.
 */
export function applyCpfCnpjMask(value) {
  const raw = String(value || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  const looksLikeCnpj = /[A-Z]/.test(raw) || raw.length > 11;

  if (looksLikeCnpj) {
    const body = raw.slice(0, 12);
    const dv = raw.slice(12).replace(/\D/g, '').slice(0, 2);
    const cleaned = (body + dv).slice(0, 14);
    return formatCnpjPartial(cleaned);
  }

  return formatCpfPartial(raw.replace(/\D/g, '').slice(0, 11));
}

export function applyCnpjMask(value) {
  const raw = String(value || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  const body = raw.slice(0, 12);
  const dv = raw.slice(12).replace(/\D/g, '').slice(0, 2);
  return formatCnpjPartial((body + dv).slice(0, 14));
}

function formatCpfPartial(cleaned) {
  return cleaned
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

function formatCnpjPartial(cleaned) {
  const len = cleaned.length;
  if (len <= 2) return cleaned;
  if (len <= 5) return cleaned.replace(/^(.{2})(.+)$/, '$1.$2');
  if (len <= 8) return cleaned.replace(/^(.{2})(.{3})(.+)$/, '$1.$2.$3');
  if (len <= 12) return cleaned.replace(/^(.{2})(.{3})(.{3})(.+)$/, '$1.$2.$3/$4');
  return cleaned.replace(/^(.{2})(.{3})(.{3})(.{4})(.+)$/, '$1.$2.$3/$4-$5');
}

function calcCheckDigit(base, length) {
  let sum = 0;
  let weight = 2;
  for (let i = length - 1; i >= 0; i--) {
    const num = base.charCodeAt(i) - 48;
    sum += num * weight;
    weight++;
    if (weight === 10) {
      weight = 2;
    }
  }
  const remainder = sum % 11;
  if (remainder === 0 || remainder === 1) {
    return '0';
  }
  return String.fromCharCode((11 - remainder) + 48);
}

export function isValidCNPJ(cnpj) {
  if (cnpj == null || cnpj === '') {
    return false;
  }
  const normalized = normalizeCNPJ(cnpj);
  if (normalized == null || normalized.length !== 14) {
    return false;
  }
  if (!CNPJ_PATTERN.test(normalized)) {
    return false;
  }
  if (new Set(normalized).size === 1) {
    return false;
  }
  const dig13 = calcCheckDigit(normalized, 12);
  const dig14 = calcCheckDigit(normalized, 13);
  return dig13 === normalized.charAt(12) && dig14 === normalized.charAt(13);
}
